// Margin Studio v3 — per-SKU pricing popup. Core control is a MARGIN-% slider
// (hard floor → max), a 4-band coloured track, big hero figures, %M/%D/£ set
// control + Floor/Tier/Retail presets, and graceful no-retail handling.
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, RotateCcw, AlertTriangle, Sparkles, TrendingUp } from "lucide-react";
import {
  GBP, unitFromCostMargin, tierUnitFromBase, marginFromUnit, discountFromUnit, MARGIN_BANDS, bandOf, effectiveCost,
} from "../_quoteHelpers";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

export default function MarginStudio({ open, line, tierInfo, rules, onApply, onClose, rebatePct = 0, supplierSet = false }) {
  const listCost = Number(line?.cost) || 0;
  const cost = effectiveCost(listCost, rebatePct);   // rebate-aware "true" cost
  const hasRebate = cost < listCost - 0.001;
  const retail = Number(line?.basePrice) || 0;
  const hasRetail = retail > 0;
  const tierDisc = (tierInfo?.matched && !tierInfo?.custom) ? Number(tierInfo.disc) : null;
  const tierPrice = (hasRetail && tierDisc != null) ? tierUnitFromBase(retail, tierDisc) : null;

  const hardPct = Number(rules?.hard_floor_pct ?? 3);
  const softPct = Number(rules?.soft_floor_pct ?? 6);
  const ceiling = Number(rules?.ceiling_pct ?? 30);

  // Retail margin is measured against the same (effective) cost basis.
  const retailMargin = hasRetail ? marginFromUnit(retail, cost) : null;

  const [unit, setUnit] = useState(Number(line?.unitPrice) || 0);
  const [basis, setBasis] = useState("margin");
  const [basisVal, setBasisVal] = useState("");
  const [confirming, setConfirming] = useState(false);
  const trackRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    if (open) { setUnit(Number(line?.unitPrice) || 0); setConfirming(false); setBasis("margin"); setBasisVal(""); }
  }, [open, line]);

  const margin = marginFromUnit(unit, cost);
  const band = bandOf(margin);
  const discOffRetail = hasRetail ? discountFromUnit(retail, unit) : null;
  const vsTier = (tierPrice && tierPrice > 0) ? ((unit - tierPrice) / tierPrice) * 100 : null;
  const cashPerUnit = unit - cost;
  const belowHard = margin < hardPct - 0.001;

  // Slider axis = MARGIN %. A wide, always-draggable range that ALWAYS contains
  // the current margin (including negative), so the handle can never freeze —
  // even if a manual price was set well below cost.
  const baseMax = Math.max(ceiling, hasRetail ? retailMargin : 0, 20);
  const axisMax = Math.max(baseMax, Math.ceil(margin) + 2);
  const axisMin = Math.min(-15, Math.floor(margin) - 2);

  const posOf = (m) => clamp((m - axisMin) / (axisMax - axisMin || 1), 0, 1);
  const marginAt = (pos) => axisMin + clamp(pos, 0, 1) * (axisMax - axisMin);
  const unitFromMargin = (m) => r2(unitFromCostMargin(cost, m));

  const applyFromPointer = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pos = (clientX - rect.left) / rect.width;
    setUnit(unitFromMargin(marginAt(pos)));
    setConfirming(false);
  }, [axisMin, axisMax, cost]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const move = (e) => { if (dragging.current) applyFromPointer(e.clientX); };
    const up = () => { dragging.current = false; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("blur", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("blur", up);
    };
  }, [open, applyFromPointer]);

  const setByBasis = (b, v) => {
    setBasis(b); setBasisVal(v); setConfirming(false);
    const num = Number(v);
    if (v === "" || isNaN(num)) return;
    if (b === "margin") setUnit(unitFromMargin(num));
    else if (b === "discount") setUnit(hasRetail ? r2(tierUnitFromBase(retail, num)) : unit);
    else setUnit(r2(num));
  };
  const preset = (kind) => {
    setConfirming(false); setBasisVal("");
    if (kind === "floor") setUnit(unitFromMargin(hardPct));
    else if (kind === "tier" && tierPrice != null) setUnit(r2(tierPrice));
    else if (kind === "retail" && hasRetail) setUnit(r2(retail));
  };
  const resetToTier = () => { if (tierPrice != null) { setUnit(r2(tierPrice)); setConfirming(false); } };

  const isCustom = !(tierPrice != null && Math.abs(unit - tierPrice) < 0.005);
  const commit = (mode) => {
    onApply({ unitPrice: r2(unit), margin: (listCost > 0 ? r2(margin) : null), mode, ...(mode === "tier" ? { tierKey: tierInfo.tier, tierDisc: tierInfo.disc } : {}) });
    onClose();
  };
  const handleDone = () => {
    // Floors never block — always allow below-floor via a one-tap confirm.
    if (belowHard && !confirming) { setConfirming(true); return; }
    const onTier = tierPrice != null && Math.abs(unit - tierPrice) < 0.005;
    commit(onTier ? "tier" : "custom");
  };

  if (!open) return null;

  // Track gradient: 4 bands mapped onto the margin axis.
  const bandStop = (m) => `${clamp((m - axisMin) / (axisMax - axisMin || 1), 0, 1) * 100}%`;
  const gradient = `linear-gradient(90deg,
    #ef4444 0%, #ef4444 ${bandStop(5)},
    #f59e0b ${bandStop(5)}, #f59e0b ${bandStop(10)},
    #34d399 ${bandStop(10)}, #34d399 ${bandStop(15)},
    #2dd4bf ${bandStop(15)}, #2dd4bf 100%)`;

  const ctx = [
    hasRebate ? `Cost ${GBP(listCost)} → true ${GBP(cost)} (−${r2(rebatePct)}% rebate)` : `Cost ${GBP(cost)}`,
    tierPrice != null ? `Tier ${GBP(tierPrice)} (${tierInfo.tier} −${tierInfo.disc}%)` : (tierInfo?.custom ? `${tierInfo.tier} · custom` : "No tier"),
    hasRetail ? `Retail ${GBP(retail)}` : "No retail on file",
    `Floor ${hardPct}%`,
  ].join("   ·   ");

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.68)", backdropFilter: "blur(5px)" }}
         onClick={onClose} data-testid="margin-studio-overlay">
      <div onClick={(e) => e.stopPropagation()} data-testid="margin-studio"
           style={{ width: 640, maxWidth: "100%", maxHeight: "94vh", overflowY: "auto", background: "var(--t2-surface2, #0E1524)", border: "1px solid var(--t2-border2)", borderRadius: 16 }}>
        {/* identity */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "18px 22px 14px", borderBottom: "1px solid var(--t2-border)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles className="w-5 h-5" style={{ color: "var(--t2-orange-ink)" }} />
              <span className="t2-mono" style={{ fontSize: 14, fontWeight: 800, color: "var(--t2-orange-ink)" }}>{line?.sku || "—"}</span>
              <span className="t2-mono" style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
                background: isCustom ? "rgba(20,184,166,0.14)" : "rgba(255,104,32,0.14)",
                color: isCustom ? "var(--t2-teal)" : "var(--t2-orange)",
                border: `1px solid ${isCustom ? "rgba(20,184,166,0.4)" : "rgba(255,104,32,0.4)"}` }}>
                {isCustom ? "CUSTOM" : "TIER"}
              </span>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--t2-white)", marginTop: 6, maxWidth: 480, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={line?.name}>{line?.name}</div>
            <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey3)", marginTop: 5 }} data-testid="margin-studio-context">{ctx}</div>
          </div>
          <button onClick={onClose} data-testid="margin-studio-close" style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer", padding: 4 }}><X className="w-5 h-5" /></button>
        </div>

        {/* hero */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: "var(--t2-border)", borderBottom: "1px solid var(--t2-border)" }}>
          {[["Unit price", GBP(unit), "var(--t2-white)", "margin-studio-hero-unit"],
            ["Margin", `${r2(margin)}%`, band.colour, "margin-studio-hero-margin"],
            ["Line total", GBP(unit * (Number(line?.qty) || 1)), "var(--t2-orange)", "margin-studio-hero-total"]].map(([lbl, val, col, tid]) => (
            <div key={lbl} style={{ background: "var(--t2-surface2, #0E1524)", padding: "16px 14px", textAlign: "center" }}>
              <div className="t2-mono" style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".12em", color: "var(--t2-grey4)" }}>{lbl}</div>
              <div className="t2-mono" data-testid={tid} style={{ fontSize: 28, fontWeight: 800, color: col, marginTop: 6, lineHeight: 1 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* slider */}
        <div style={{ padding: "34px 26px 10px" }}>
          <div ref={trackRef} data-testid="margin-studio-track"
               onPointerDown={(e) => { dragging.current = true; applyFromPointer(e.clientX); }}
               style={{ position: "relative", height: 14, borderRadius: 999, background: gradient, cursor: "pointer", margin: "22px 0" }}>
            {tierPrice != null && retailMargin != null && (
              <Tick pos={posOf(marginFromUnit(tierPrice, cost))} label="tier" colour="#ffffff" strong />
            )}
            <Tick pos={posOf(hardPct)} label="floor" colour="rgba(255,255,255,0.5)" />
            {posOf(0) > 0.01 && posOf(0) < 0.99 && (
              <Tick pos={posOf(0)} label="cost" colour="rgba(239,68,68,0.7)" />
            )}
            <div style={{ position: "absolute", top: "50%", left: `${posOf(margin) * 100}%`, transform: "translate(-50%,-50%)" }}>
              <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }} className="t2-mono">
                <span data-testid="margin-studio-bubble" style={{ fontSize: 13, fontWeight: 800, color: "var(--t2-white)", background: "var(--t2-surface3, #1a2436)", border: "1px solid var(--t2-border2)", borderRadius: 8, padding: "4px 10px" }}>
                  {GBP(unit)} · {r2(margin)}%
                </span>
              </div>
              <div data-testid="margin-studio-handle" onPointerDown={(e) => { e.stopPropagation(); dragging.current = true; }}
                   style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--t2-white)", border: `4px solid ${band.colour}`, cursor: "grab", boxShadow: "0 3px 10px rgba(0,0,0,.55)" }} />
            </div>
          </div>
          <div className="t2-mono" style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--t2-grey4)" }}>
            <span>{r2(axisMin)}% · {GBP(unitFromMargin(axisMin))}</span>
            <span>{hasRetail ? `Retail ${GBP(retail)} · ${r2(retailMargin)}%` : `Ceiling ${r2(ceiling)}%`}</span>
          </div>

          {/* band legend */}
          <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }} data-testid="margin-studio-legend">
            {MARGIN_BANDS.map((b) => (
              <span key={b.key} className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 9.5, color: "var(--t2-grey3)" }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: b.colour }} />
                {b.label} {b.max === Infinity ? `${b.min}%+` : `${b.min}–${b.max}%`}
              </span>
            ))}
          </div>

          {/* secondary read-outs */}
          <div className="t2-mono" data-testid="margin-studio-readouts" style={{ display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap", fontSize: 11, color: "var(--t2-grey3)", marginTop: 16 }}>
            {discOffRetail != null && <span>disc off retail <b style={{ color: "var(--t2-grey1)" }}>{r2(discOffRetail)}%</b></span>}
            {vsTier != null && <span>vs tier <b style={{ color: vsTier < 0 ? "var(--t2-teal)" : "var(--t2-brass)" }}>{vsTier > 0 ? "+" : ""}{r2(vsTier)}%</b></span>}
            <span>cash / unit <b style={{ color: cashPerUnit < 0 ? "var(--t2-red)" : "var(--t2-ok)" }}>{GBP(cashPerUnit)}</b></span>
          </div>
        </div>

        {/* set control */}
        <div style={{ padding: "10px 26px 16px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--t2-border2)" }}>
            {[["margin", "%M"], ["discount", "%D"], ["unit", "£"]].map(([b, lbl]) => (
              <button key={b} type="button" onClick={() => { setBasis(b); setBasisVal(""); }} data-testid={`margin-studio-basis-${b}`}
                disabled={b === "discount" && !hasRetail}
                className="t2-mono" style={{ fontSize: 12, fontWeight: 700, padding: "8px 14px", border: "none", cursor: (b === "discount" && !hasRetail) ? "not-allowed" : "pointer",
                  background: basis === b ? "var(--t2-surface3, #1a2436)" : "transparent", color: basis === b ? "var(--t2-white)" : "var(--t2-grey3)", opacity: (b === "discount" && !hasRetail) ? 0.4 : 1 }}>{lbl}</button>
            ))}
          </div>
          <input type="number" step="0.1" value={basisVal}
            placeholder={basis === "unit" ? r2(unit) : basis === "margin" ? r2(margin) : r2(discOffRetail || 0)}
            onChange={(e) => setByBasis(basis, e.target.value)} data-testid="margin-studio-set-input"
            className="t2-input t2-mono" style={{ width: 110, padding: "8px 12px", fontSize: 15, fontWeight: 700, textAlign: "right" }} />
          <span style={{ flex: 1 }} />
          <span className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey4)" }}>presets</span>
          <button type="button" onClick={() => preset("floor")} className="t2-btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="margin-studio-preset-floor">Floor</button>
          <button type="button" onClick={() => preset("tier")} disabled={tierPrice == null} className="t2-btn-ghost" style={{ padding: "8px 14px", fontSize: 12, opacity: tierPrice == null ? 0.4 : 1, cursor: tierPrice == null ? "not-allowed" : "pointer" }} data-testid="margin-studio-preset-tier">Tier</button>
          <button type="button" onClick={() => preset("retail")} disabled={!hasRetail} className="t2-btn-ghost" style={{ padding: "8px 14px", fontSize: 12, opacity: hasRetail ? 1 : 0.4, cursor: hasRetail ? "pointer" : "not-allowed" }} data-testid="margin-studio-preset-retail">Retail</button>
        </div>

        {/* footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px 18px", borderTop: "1px solid var(--t2-border)" }}>
          <span data-testid="margin-studio-band-flag" style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 700, color: margin < 0 ? "var(--t2-red)" : band.colour }}>
            <TrendingUp className="w-4 h-4" /> {margin < 0 ? "Below cost" : band.label} · {r2(margin)}%
          </span>
          {belowHard && (
            <span data-testid="margin-studio-warn" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--t2-red-ink)" }}>
              <AlertTriangle className="w-4 h-4" /> Below floor ({hardPct}%){hasRebate ? " — vs true cost" : ""}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {isCustom && tierPrice != null && (
            <button type="button" onClick={resetToTier} className="t2-btn-ghost" style={{ padding: "8px 13px", fontSize: 12 }} data-testid="margin-studio-reset-tier">
              <RotateCcw className="w-4 h-4" /> Reset to tier
            </button>
          )}
          <button type="button" onClick={onClose} className="t2-btn-ghost" style={{ padding: "8px 16px", fontSize: 13 }} data-testid="margin-studio-cancel">Cancel</button>
          <button type="button" onClick={handleDone} data-testid="margin-studio-done"
            style={{ padding: "8px 20px", fontSize: 13, fontWeight: 700, borderRadius: 9, border: "none",
              cursor: "pointer",
              background: confirming ? "var(--t2-red)" : "var(--t2-orange)", color: "#000" }}>
            {confirming ? "Confirm below-floor" : "Done"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Tick({ pos, label, colour, strong }) {
  return (
    <div style={{ position: "absolute", top: "50%", left: `${pos * 100}%`, transform: "translate(-50%,-50%)", pointerEvents: "none" }}>
      <div style={{ width: strong ? 3 : 2, height: strong ? 22 : 16, background: colour, borderRadius: 2, boxShadow: "0 0 0 1px rgba(0,0,0,.4)" }} />
      <span className="t2-mono" style={{ position: "absolute", top: strong ? 24 : 18, left: "50%", transform: "translateX(-50%)", fontSize: 9, color: colour, whiteSpace: "nowrap" }}>{label}</span>
    </div>
  );
}
